import type { Permission } from '@shared/types';
import type { IconName } from '../components/Icon';

/**
 * 顶栏一级导航。
 *
 * 版式照阿里云控制台：**顶栏 + 一级导航两行**，没有左侧栏；
 * 需要分栏的页面在内容区里再用标签组（见 components/TabGroup.tsx）。
 */

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** 该入口需要的权限位；不具备时整项不渲染（体验层，安全层在 Worker） */
  permission: Permission;
  description: string;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/',
    label: '概览',
    icon: 'apps',
    permission: 'org.view',
    description: '账号与组织摘要、安全待办、最近活动',
  },
  {
    to: '/members',
    label: '身份与访问',
    icon: 'users',
    permission: 'member.view',
    description: '成员与邀请、组织资料、角色与权限',
  },
  {
    to: '/security',
    label: '安全',
    icon: 'shield',
    permission: 'org.view',
    description: '口令、两步验证、会话与设备、登录历史',
  },
  {
    to: '/applications',
    label: '接入',
    icon: 'key',
    permission: 'app.view',
    description: 'OIDC 接入应用与操作记录',
  },
  {
    to: '/fleet',
    label: '资源',
    icon: 'drone',
    permission: 'org.view',
    description: '机队入口、订阅与用量、工单与支持',
  },
];

/** 侧栏式的二级导航：每个一级入口下挂若干页面，用标签组呈现。 */
export interface SubNavItem {
  to: string;
  label: string;
  permission?: Permission;
}

export const SUB_NAV: Record<string, readonly SubNavItem[]> = {
  '/members': [
    { to: '/members', label: '成员与邀请', permission: 'member.view' },
    { to: '/org', label: '组织资料', permission: 'org.view' },
    { to: '/org/roles', label: '角色与权限', permission: 'role.view' },
    { to: '/org/verification', label: '企业资质', permission: 'org.view' },
  ],
  '/org': [
    { to: '/members', label: '成员与邀请', permission: 'member.view' },
    { to: '/org', label: '组织资料', permission: 'org.view' },
    { to: '/org/roles', label: '角色与权限', permission: 'role.view' },
    { to: '/org/verification', label: '企业资质', permission: 'org.view' },
  ],
  '/org/roles': [
    { to: '/members', label: '成员与邀请', permission: 'member.view' },
    { to: '/org', label: '组织资料', permission: 'org.view' },
    { to: '/org/roles', label: '角色与权限', permission: 'role.view' },
    { to: '/org/verification', label: '企业资质', permission: 'org.view' },
  ],
  '/org/verification': [
    { to: '/members', label: '成员与邀请', permission: 'member.view' },
    { to: '/org', label: '组织资料', permission: 'org.view' },
    { to: '/org/roles', label: '角色与权限', permission: 'role.view' },
    { to: '/org/verification', label: '企业资质', permission: 'org.view' },
  ],
  '/security': [
    { to: '/security', label: '安全设置' },
    { to: '/security/sessions', label: '会话与设备' },
    { to: '/security/history', label: '登录历史' },
  ],
  '/security/sessions': [
    { to: '/security', label: '安全设置' },
    { to: '/security/sessions', label: '会话与设备' },
    { to: '/security/history', label: '登录历史' },
  ],
  '/security/history': [
    { to: '/security', label: '安全设置' },
    { to: '/security/sessions', label: '会话与设备' },
    { to: '/security/history', label: '登录历史' },
  ],
  '/applications': [
    { to: '/applications', label: '应用与接入', permission: 'app.view' },
    { to: '/audit', label: '操作记录', permission: 'audit.view' },
  ],
  '/audit': [
    { to: '/applications', label: '应用与接入', permission: 'app.view' },
    { to: '/audit', label: '操作记录', permission: 'audit.view' },
  ],
  '/fleet': [
    { to: '/fleet', label: '机队' },
    { to: '/billing', label: '订阅与用量', permission: 'billing.view' },
    { to: '/support', label: '工单与支持' },
  ],
  '/billing': [
    { to: '/fleet', label: '机队' },
    { to: '/billing', label: '订阅与用量', permission: 'billing.view' },
    { to: '/support', label: '工单与支持' },
  ],
  '/support': [
    { to: '/fleet', label: '机队' },
    { to: '/billing', label: '订阅与用量', permission: 'billing.view' },
    { to: '/support', label: '工单与支持' },
  ],
};

/** 找出当前路径所属的一级导航项。 */
export function activeNavItem(pathname: string): NavItem | undefined {
  if (pathname === '/') return NAV_ITEMS[0];
  return NAV_ITEMS.find((item) => item.to !== '/' && pathname.startsWith(item.to));
}

/** 找出当前路径该显示的二级标签。 */
export function subNavFor(pathname: string): readonly SubNavItem[] {
  const group = SUB_NAV[pathname];
  if (group) return group;
  const match = Object.keys(SUB_NAV)
    .filter((key) => key !== '/' && pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];
  return match ? (SUB_NAV[match] as readonly SubNavItem[]) : [];
}
